import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Checkbox,
  Radio,
  message,
  DatePicker,
  Tooltip
} from 'antd'
import styled from 'styled-components'
import api from '../utils/api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import cityData from '../utils/cityData'
import { useAuth } from '../hooks/AuthContext'
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { fromPicker } from '../utils/timezone'

const { Option } = Select
const { RangePicker } = DatePicker

const { TextArea } = Input

const PageContainer = styled.div`
  padding: 0 24px;
  background: #fff;
`

const StyledForm = styled(Form)`
  max-width: 600px;
`

// 在组件开始时添加 dayjs 插件
dayjs.extend(utc)
dayjs.extend(timezone)

const RecruitmentSetup = () => {
  const [form] = Form.useForm()
  const [recruitmentType, setRecruitmentType] = useState(null)
  const [experienceModelType, setExperienceModelType] = useState(null)
  const [salaryType, setSalaryType] = useState(null)
  const [reciprocalCompensationType, setReciprocalCompensationType] =
    useState(null)
  const [selectedCity, setSelectedCity] = useState(null)
  const [districts, setDistricts] = useState([])
  const { businessId } = useAuth()
  const [timeVisible, setTimeVisible] = useState(false)
  const [selectedTime, setSelectedTime] = useState(['', ''])
  const [products, setProducts] = useState([])
  const [employees, setEmployees] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableTimes, setAvailableTimes] = useState([])

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get(`/api/business_B/${businessId}/products`)
        setProducts(response.data)
      } catch (error) {
        console.error('獲取產品失敗:', error)
        message.error('獲取招募項目失敗')
      }
    }

    const fetchEmployees = async () => {
      try {
        const response = await api.get(`/api/employees/${businessId}`)
        setEmployees(response.data)
      } catch (error) {
        console.error('獲取員工失敗:', error)
        message.error('獲取員工列表失敗')
      }
    }

    fetchProducts()
    fetchEmployees()
  }, [businessId])

  useEffect(() => {
    if (selectedCity) {
      const cityDistricts = cityData.districts.find(
        (d) => d.name === selectedCity
      )
      setDistricts(cityDistricts ? cityDistricts.areas : [])
    } else {
      setDistricts([])
    }
  }, [selectedCity])

  const timeOptions = [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00',
    '07:00',
    '08:00',
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00',
    '20:00',
    '21:00',
    '22:00',
    '23:00'
  ]

  const handleTimeConfirm = (value) => {
    setSelectedTime(value)
    setTimeVisible(false)
  }

  const onFinish = async (values) => {
    setIsSubmitting(true)
    try {
      // 基本資料格式化
      let formattedValues = {
        title: values.title,
        recruitmentType: values.recruitmentType,
        description: values.description,
        recruitmentItems: values.serviceItems
          .map((itemId) => {
            const product = products.find((p) => p._id === itemId)
            return product ? { id: product._id, name: product.name } : null
          })
          .filter((item) => item !== null),
        status: 'active',
        location: {
          city: values.city,
          district: values.district
        },
        // 所有類型都需要 dateTimeRange
        // ⚠️ 這裡原本是 .tz('Asia/Taipei')，方向是反的：.tz() 保留瞬間而改寫
        // 牆上時間，招募者人在 UTC+7 挑的 14:00 會被存成台灣 13:00。
        // fromPicker 才是「保留使用者看到的時間、換上台灣時區」。
        dateTimeRange: {
          start: fromPicker(values.dateTimeRange[0]).toISOString(),
          end: fromPicker(values.dateTimeRange[1]).toISOString()
        }
      }

      // 如果是體驗模特或互惠模特，添加 availableTimes
      if (values.recruitmentType !== 'employee') {
        formattedValues.availableTimes = values.availableTimes.map((slot) => ({
          date: fromPicker(slot.date).format('YYYY-MM-DD'),
          times: slot.times
        }))

        // 添加服務提供者資訊
        formattedValues.serviceProvider = (() => {
          const employee = employees.find(
            (e) => e._id === values.serviceProvider
          )
          return employee
            ? {
                id: employee._id,
                username: employee.username,
                name: employee.name
              }
            : null
        })()
      }

      // 處理其他招聘類型特定的字段
      if (values.recruitmentType === 'experienceModel') {
        formattedValues.experienceModelType = values.experienceModelType
        if (values.experienceModelType === 'paid') {
          formattedValues.experienceModelFee = values.serviceFee
        }
      } else if (values.recruitmentType === 'reciprocalModel') {
        formattedValues.reciprocalOptions = values.mutualBenefitOptions
        formattedValues.reciprocalCompensationType =
          values.mutualBenefitPaymentType
        if (values.mutualBenefitPaymentType === 'compensation') {
          formattedValues.reciprocalCompensationAmount =
            values.mutualBenefitPayment
        }
      } else if (values.recruitmentType === 'employee') {
        formattedValues.salaryType = values.salaryType
        if (values.salaryType === 'fixed') {
          formattedValues.salaryAmount = values.salary
        }
        formattedValues.experienceYears = values.requiredExperience
      }

      const response = await api.post(
        `/api/business_B/${businessId}/recruitments`,
        formattedValues
      )
      message.success('招聘需求創建成功')
      console.log('服務器響應:', response.data)

      // 清空表單和重置狀態
      form.resetFields()
      setRecruitmentType(null)
      setExperienceModelType(null)
      setSalaryType(null)
      setReciprocalCompensationType(null)
      setSelectedCity(null)
      setDistricts([])
      setAvailableTimes([])
    } catch (error) {
      message.error('創建招聘需求失敗')
      console.error('創建招聘需求時出錯:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onRecruitmentTypeChange = (value) => {
    setRecruitmentType(value)
    form.setFieldsValue({
      experienceModelType: undefined,
      mutualBenefitOptions: [],
      salaryType: undefined,
      salary: undefined,
      serviceFee: undefined,
      requiredExperience: undefined,
      mutualBenefitPaymentType: undefined,
      mutualBenefitPayment: undefined
    })
  }

  const onExperienceModelTypeChange = (e) => {
    setExperienceModelType(e.target.value)
    form.setFieldsValue({ serviceFee: undefined })
  }

  const onSalaryTypeChange = (e) => {
    setSalaryType(e.target.value)
    form.setFieldsValue({ salary: undefined })
  }

  const onReciprocalCompensationTypeChange = (e) => {
    setReciprocalCompensationType(e.target.value)
    form.setFieldsValue({ mutualBenefitPayment: undefined })
  }

  const addTimeSlot = () => {
    const newTimes = [...availableTimes, { date: null, times: [] }]
    setAvailableTimes(newTimes)
    form.setFieldsValue({ availableTimes: newTimes })
  }

  const removeTimeSlot = (index) => {
    const newTimes = [...availableTimes]
    newTimes.splice(index, 1)
    setAvailableTimes(newTimes)
    form.setFieldsValue({ availableTimes: newTimes })
  }

  const onDateChange = (date, index) => {
    const newTimes = [...availableTimes]
    newTimes[index].date = date ? dayjs(date).tz('Asia/Taipei') : null
    setAvailableTimes(newTimes)
    form.setFieldsValue({ availableTimes: newTimes })
    console.log('date', newTimes)
  }

  const onTimesChange = (times, index) => {
    const newTimes = [...availableTimes]
    newTimes[index].times = times
    setAvailableTimes(newTimes)
    form.setFieldsValue({ availableTimes: newTimes })
    console.log('time', newTimes)
  }

  return (
    <PageContainer>
      <StyledForm form={form} layout='vertical' onFinish={onFinish}>
        <Form.Item name='title' label='標題' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name='recruitmentType'
          label='招聘類型'
          rules={[{ required: true }]}
        >
          <Select onChange={onRecruitmentTypeChange}>
            <Select.Option value='employee'>招募員工</Select.Option>
            <Select.Option value='experienceModel'>
              招募體驗模特兒
            </Select.Option>
            <Select.Option value='reciprocalModel'>
              招募互惠模特兒
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name='dateTimeRange'
          label='招聘期間'
          rules={[{ required: true, message: '請選擇招聘時間範圍' }]}
        >
          <RangePicker showTime format='YYYY-MM-DD HH:mm' />
        </Form.Item>
        {recruitmentType && recruitmentType !== 'employee' && (
          <Form.Item
            name='availableTimes'
            label={
              <span>
                可供預約時段
                <Tooltip title='請注意，此預約時段與店家服務預約時間無關，請自行檢查是否有衝突之處，可自行至店家休假日刪減欲排除的時段'>
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject('請添加至少一個可供預約時段')
                  }
                  // 確保每個時間段都有日期和時間
                  const isValid = value.every(
                    (slot) => slot.date && slot.times && slot.times.length > 0
                  )
                  if (!isValid) {
                    return Promise.reject('請確保每個時間段都有選擇日期和時間')
                  }
                  return Promise.resolve()
                }
              }
            ]}
          >
            <Tooltip title='請注意，此預約時段與店家服務預約時間無關，請自行檢查是否有衝突之處，可自行至店家休假日刪減欲排除的時段'>
              <div>
                {availableTimes.map((slot, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <DatePicker
                      value={slot.date ? dayjs(slot.date) : null}
                      onChange={(date) => onDateChange(date, index)}
                      format='YYYY-MM-DD'
                      style={{ marginRight: 8 }}
                    />
                    <Select
                      mode='multiple'
                      style={{ width: 300 }}
                      placeholder='選擇時間段'
                      value={slot.times}
                      onChange={(times) => onTimesChange(times, index)}
                    >
                      {timeOptions.map((time) => (
                        <Option key={time} value={time}>
                          {time}
                        </Option>
                      ))}
                    </Select>
                    <Button
                      type='link'
                      onClick={() => removeTimeSlot(index)}
                      icon={<DeleteOutlined />}
                    />
                  </div>
                ))}
                <Button type='dashed' onClick={addTimeSlot} block>
                  添加時間段
                </Button>
              </div>
            </Tooltip>
          </Form.Item>
        )}
        <Form.Item
          name='requiredNumber'
          label='需求人數'
          rules={[{ required: true }]}
        >
          <InputNumber min={1} max={100} />
        </Form.Item>
        {recruitmentType === 'experienceModel' && (
          <Form.Item
            name='experienceModelType'
            label='體驗模特兒類型'
            rules={[{ required: true }]}
          >
            <Radio.Group onChange={onExperienceModelTypeChange}>
              <Radio value='paid'>收取施作費用</Radio>
              <Radio value='free'>免費</Radio>
            </Radio.Group>
          </Form.Item>
        )}
        {recruitmentType === 'experienceModel' &&
          experienceModelType === 'paid' && (
            <Form.Item
              name='serviceFee'
              label='施作費用（元）'
              rules={[{ required: true }]}
            >
              <InputNumber min={0} step={100} />
            </Form.Item>
          )}
        {recruitmentType === 'reciprocalModel' && (
          <>
            <Form.Item name='mutualBenefitOptions' label='互惠選項'>
              <Checkbox.Group>
                <Checkbox value='socialMediaPost'>
                  IG/FB/Google其他管道發文
                </Checkbox>
                <Checkbox value='allowPhotoshoot'>
                  同意店家拍攝作品照並上傳社群網路
                </Checkbox>
              </Checkbox.Group>
            </Form.Item>
            <Form.Item
              name='mutualBenefitPaymentType'
              label='報酬選項'
              rules={[{ required: true }]}
            >
              <Radio.Group onChange={onReciprocalCompensationTypeChange}>
                <Radio value='noCost'>雙方不收取費用</Radio>
                <Radio value='compensation'>提供模特兒報酬</Radio>
              </Radio.Group>
            </Form.Item>
            {reciprocalCompensationType === 'compensation' && (
              <Form.Item
                name='mutualBenefitPayment'
                label='模特兒報酬（元）'
                rules={[{ required: true }]}
              >
                <InputNumber min={0} step={100} />
              </Form.Item>
            )}
          </>
        )}
        <Form.Item
          name='serviceItems'
          label='招聘技術/施作項目'
          rules={[{ required: true, message: '請選擇至少一個招募項目' }]}
        >
          <Select mode='multiple' placeholder='選擇招募項目'>
            {products.map((product) => (
              <Option key={product._id} value={product._id}>
                {product.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {(recruitmentType === 'experienceModel' ||
          recruitmentType === 'reciprocalModel') && (
          <Form.Item
            name='serviceProvider'
            label='施作人員'
            rules={[{ required: true, message: '請選擇施作人員' }]}
          >
            <Select placeholder='選擇施作人員'>
              {employees.map((employee) => (
                <Option key={employee._id} value={employee._id}>
                  {employee.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {recruitmentType === 'employee' && (
          <>
            <Form.Item
              name='salaryType'
              label='薪資報酬'
              rules={[{ required: true }]}
            >
              <Radio.Group onChange={onSalaryTypeChange}>
                <Radio value='negotiable'>面議</Radio>
                <Radio value='fixed'>固定金額</Radio>
              </Radio.Group>
            </Form.Item>
            {salaryType === 'fixed' && (
              <Form.Item
                name='salary'
                label='薪資金額（元/月）'
                rules={[{ required: true }]}
              >
                <InputNumber min={0} step={1000} />
              </Form.Item>
            )}
            <Form.Item
              name='requiredExperience'
              label='所需經驗（年）'
              rules={[{ required: true }]}
            >
              <InputNumber min={0} max={10} />
            </Form.Item>
          </>
        )}
        <Form.Item
          name='city'
          label='城市'
          rules={[{ required: true, message: '請選擇城市' }]}
        >
          <Select
            placeholder='選擇城市'
            onChange={(value) => setSelectedCity(value)}
          >
            {cityData.counties.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='district'
          label='區域'
          rules={[{ required: true, message: '請擇區域' }]}
        >
          <Select placeholder='選擇區域'>
            {districts.map((district) => (
              <Option key={district} value={district}>
                {district}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='description'
          label='招聘描述/需求/注意事項'
          rules={[{ required: true }]}
        >
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item>
          <Button type='primary' htmlType='submit' disabled={isSubmitting}>
            {isSubmitting ? '創建中...' : '創建招聘需求'}
          </Button>
        </Form.Item>
      </StyledForm>
    </PageContainer>
  )
}

export default RecruitmentSetup
